import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Per-user, per-board column-order storage + drag-to-reorder UX.
 *
 * Design goals (intentionally mirrors `useHiddenColumns`):
 *   - Persist per logged-in user so two PMs on the same desk don't
 *     clobber each other.
 *   - Re-apply the saved order on every render via direct DOM
 *     manipulation, so we don't have to refactor every board's
 *     hard-coded `<th>` / `<td>` JSX into a config-driven render.
 *
 * How the reorder works:
 *   Each board's `<th>` and `<td>` already carries a `col-{key}` CSS
 *   class (the same hook `useHiddenColumns` keys off). After every
 *   React render we walk every `<tr>` inside `[data-board-table="X"]`
 *   and reorder ONLY the cells whose className includes `col-`. Cells
 *   without a `col-` class (e.g. the leftmost Project # cell which
 *   carries the territory color border, or the sticky right-side
 *   Actions cell) stay anchored in place.
 *
 * Drag UX: every `<th class="col-…">` becomes `draggable`. Dragging
 * one over another inserts the dragged column before the target.
 * The `<th>` already has an `onClick={handleSort}` — `draggable`
 * does not interfere with simple clicks (a click without movement
 * still fires onClick after the dragend pair).
 */
export function useColumnOrder(board, userEmail, defaultOrder) {
  const key = `restorepm:columns-order:${userEmail || 'anon'}:${board}`;

  const read = (k) => {
    try {
      const raw = localStorage.getItem(k);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch { /* ignore */ }
    return null;
  };

  const [order, setOrder] = useState(() => read(key) || defaultOrder);

  // Re-read whenever the user changes (logged-in email arrives late).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setOrder(read(key) || defaultOrder); }, [key]);

  // Only persist on explicit user actions; key-change re-reads must
  // not write the stale state back into the new key.
  const userActionRef = useRef(false);
  useEffect(() => {
    if (!userActionRef.current) return;
    userActionRef.current = false;
    try { localStorage.setItem(key, JSON.stringify(order)); }
    catch { /* ignore */ }
  }, [key, order]);

  // Helper: extract the col-XXX key from a cell's className, or null.
  const colKeyOf = (el) => {
    const cls = el && el.className;
    if (typeof cls !== 'string') return null;
    const m = cls.match(/(?:^|\s)col-([A-Za-z0-9_-]+)/);
    return m ? m[1] : null;
  };

  // Note: cell reordering itself is NOT done via DOM manipulation any
  // more. React's reconciler patches `<td>` content in place when the
  // tbody re-renders, which silently undoes any post-render node
  // moves and causes headers to fall out of sync with body cells (the
  // "Invoice Sent Date column shows the wrong date" bug). Boards now
  // render cells in `order` directly via a column-config map, so the
  // hook only needs to manage the order state and wire up drag UX.

  // Wire up drag-and-drop on the thead's <th class="col-…"> cells.
  // Re-runs after every render (cheap — handlers are idempotent).
  useEffect(() => {
    const tables = document.querySelectorAll(`table[data-board-table="${board}"]`);
    const cleanups = [];
    tables.forEach((table) => {
      const ths = table.querySelectorAll('thead th');
      ths.forEach((th) => {
        const colKey = colKeyOf(th);
        if (!colKey) return;
        if (th.__rpmDragWired) return; // avoid duplicate listeners on re-render
        th.__rpmDragWired = true;
        th.setAttribute('draggable', 'true');
        th.setAttribute('data-col-drag', colKey);
        const prevTitle = th.title;
        th.title = prevTitle || 'Drag to reorder · click to sort';

        const onDragStart = (e) => {
          e.dataTransfer.effectAllowed = 'move';
          try { e.dataTransfer.setData('text/plain', colKey); } catch { /* ignore */ }
          th.classList.add('rpm-col-dragging');
        };
        const onDragEnd = () => {
          th.classList.remove('rpm-col-dragging');
          table.querySelectorAll('thead th.rpm-col-drop-target').forEach((el) => el.classList.remove('rpm-col-drop-target'));
        };
        const onDragOver = (e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          th.classList.add('rpm-col-drop-target');
        };
        const onDragLeave = () => {
          th.classList.remove('rpm-col-drop-target');
        };
        const onDrop = (e) => {
          e.preventDefault();
          th.classList.remove('rpm-col-drop-target');
          let dragged = '';
          try { dragged = e.dataTransfer.getData('text/plain'); } catch { /* ignore */ }
          if (!dragged || dragged === colKey) return;
          userActionRef.current = true;
          setOrder((prev) => {
            // Use the current set of col-keys in the thead as the
            // canonical "all known columns" list — this way we don't
            // lose columns that were never persisted (e.g. on a fresh
            // first-drag where prev was just the defaults).
            const allKeys = Array.from(table.querySelectorAll('thead th'))
              .map((el) => colKeyOf(el))
              .filter(Boolean);
            const merged = [...prev];
            allKeys.forEach((k) => { if (!merged.includes(k)) merged.push(k); });
            const without = merged.filter((k) => k !== dragged);
            const insertAt = without.indexOf(colKey);
            if (insertAt < 0) return merged; // safety
            without.splice(insertAt, 0, dragged);
            return without;
          });
        };

        th.addEventListener('dragstart', onDragStart);
        th.addEventListener('dragend', onDragEnd);
        th.addEventListener('dragover', onDragOver);
        th.addEventListener('dragleave', onDragLeave);
        th.addEventListener('drop', onDrop);
        cleanups.push(() => {
          th.removeEventListener('dragstart', onDragStart);
          th.removeEventListener('dragend', onDragEnd);
          th.removeEventListener('dragover', onDragOver);
          th.removeEventListener('dragleave', onDragLeave);
          th.removeEventListener('drop', onDrop);
          th.removeAttribute('draggable');
          th.removeAttribute('data-col-drag');
          th.__rpmDragWired = false;
          th.title = prevTitle || '';
        });
      });
    });
    return () => cleanups.forEach((c) => c());
  });

  const reset = useCallback(() => {
    userActionRef.current = true;
    setOrder(defaultOrder);
  }, [defaultOrder]);

  return { order, setOrder, reset };
}

export default useColumnOrder;
